<script setup>
    import { useGtm } from '@/composables/helpers/client-only/gtm';
    import { storeToRefs } from 'pinia';
    import { useWindowStore } from '@/stores/window';
    const { scrollY, innerHeight } = storeToRefs(useWindowStore());

    const props = defineProps({
        isStreaming: {
            type: Boolean,
            default: false
        }
    });

    const topbox = ref();
    const prompt = ref();

    const userInput = defineModel('input', { type: String, default: '' })
    const offscreen = defineModel('fixed', { type: Boolean, default: false })
    const emits = defineEmits(['sendMessage'])

    const handleEnterKey = (event) => {
        if (!event.shiftKey) {
            event.preventDefault();
            useGtm('assistant_prompt_enter_key');
            emits('sendMessage', userInput.value);
        }
    };

    const sendMessage = (event) => {
        event.preventDefault();
        if (userInput.value.trim()) {
            useGtm('assistant_send_message');
            emits('sendMessage', userInput.value);
            userInput.value = ''; // Clear input after sending
        }
    };

    watch([scrollY, innerHeight], () => {
        const el = topbox.value;
        if (!el) {
            offscreen.value = false;
            return;
        }
        const rect = el.getBoundingClientRect();
        // If the bottom of the element is above 0 or the top is below the viewport, it's offscreen
        // Element is offscreen if ANY part is not visible
        offscreen.value = rect.top < 0 || rect.bottom > innerHeight.value;
    });

    // Expose the prompt ref to parent component
    defineExpose({
        prompt
    });

</script>
<template>
    <div class="assistant-prompt min-h-42.25" ref="topbox">
        <form div class="prompt container bg-(--cream)" @submit="sendMessage" role="form" aria-label="Message input" :class="{ 'is-fixed': offscreen }" ref="prompt">
            <div class="chat-submit mb-4.25 relative rounded-(--radius) border border-(--teal)" :class="{ streaming: isStreaming }">
                <textarea
                    ref="textareaRef"
                    class="chat-prompt w-full min-h-33.25 resize-none text-(--teal) px-10 py-5 pr-28.5"
                    v-model="userInput"
                    @keydown.enter="handleEnterKey"
                    placeholder="Type your message..."
                    aria-label="Type your message"
                    :aria-describedby="isStreaming ? 'streaming-status' : null"
                    :disabled="isStreaming" />
                <span v-if="isStreaming" id="streaming-status" class="sr-only">Assistant is currently responding. Please wait.</span>
                <UButton :disabled="!userInput.trim()" color="teal" type="submit" aria-label="Send message" variant="outline">Send</UButton>
            </div>
            <div class="extras">
                <p class="legal" role="complementary" aria-label="AI accuracy disclaimer">
                    AI generated this content. As a best practice, please check all information provided outside of session recommendations as it can hallucinate and provide incorrect guidance at times. Have a question? Check out our help page for support or email us at <a href="mailto:help@inbound.com">help@inbound.com</a>.
                </p>
            </div>
        </form>
    </div>
</template>

<style>
.assistant-prompt .prompt.is-fixed {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9;
}

.assistant-prompt .chat-submit.streaming {
    border-color: var(--sage);
}

.assistant-prompt .chat-submit textarea:focus {
    outline: none;
    border: none;
    box-shadow: none;
}

.assistant-prompt .chat-submit button {
    position: absolute;
    right: 1.5rem;
    bottom: 1.25rem;
    font-size: 0.8125rem;
    font-weight: 400;
    letter-spacing: 0.065rem;
    text-transform: uppercase;
}

.assistant-prompt .legal {
    color: var(--teal-gray);
    font-size: 0.6875rem;
    font-weight: 300;
    line-height: 1.1085625rem;
    letter-spacing: 0.020625rem;
    padding: 0 2.5rem;
    margin-bottom: 1rem;
}

.assistant-prompt .button-group {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding: 0 2.5rem;
}
</style>
